import React, { useEffect, useState } from 'react';
import api from '../api/axiosConfig';
import './CompaniesPage.css';
import '../styles/rowActions.css';

// Hectáreas en producción / fumigadas: up to 8 whole digits and 2 decimals (DecimalField 10,2); comma or dot.
const HECTARES_PATTERN = /^\d{1,8}([.,]\d{1,2})?$/;
const hectaresValid = (text) => text.trim() === '' || HECTARES_PATTERN.test(text.trim());
const hectaresPayload = (text) => (text.trim() === '' ? null : text.trim().replace(',', '.'));
const hectaresText = (value) => (value == null ? '' : String(Number(value)).replace('.', ','));
const formatHectares = (value) => (value == null
  ? '—' : Number(value).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const cleanHectares = (text) => text.replace(/[^\d.,]/g, '');

export default function FarmsPage() {
  const [farms, setFarms] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  const [newName, setNewName] = useState('');
  const [newCompanyId, setNewCompanyId] = useState('');
  const [newMagap, setNewMagap] = useState('');
  const [newHectares, setNewHectares] = useState('');
  const [newFumigated, setNewFumigated] = useState('');
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCompanyId, setEditCompanyId] = useState('');
  const [editMagap, setEditMagap] = useState('');
  const [editHectares, setEditHectares] = useState('');
  const [editFumigated, setEditFumigated] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = () => {
    setLoading(true);
    return Promise.all([api.get('farms/'), api.get('companies/')])
      .then(([farmsRes, companiesRes]) => {
        setFarms(farmsRes.data);
        setCompanies(companiesRes.data);
        if (!newCompanyId && companiesRes.data.length > 0) {
          setNewCompanyId(String(companiesRes.data[0].id));
        }
      })
      .catch(() => setStatus({ type: 'error', text: 'No se pudieron cargar las fincas.' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newCompanyId || !hectaresValid(newHectares) || !hectaresValid(newFumigated)) return;
    setCreating(true);
    setStatus(null);
    try {
      const r = await api.post('farms/', {
        name: newName.trim(),
        company: Number(newCompanyId),
        magap: newMagap.trim(),
        hectares_in_production: hectaresPayload(newHectares),
        fumigated_hectares: hectaresPayload(newFumigated),
      });
      setFarms((fs) => [...fs, r.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewName('');
      setNewMagap('');
      setNewHectares('');
      setNewFumigated('');
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo crear la finca.' });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (f) => {
    setEditingId(f.id);
    setEditName(f.name);
    setEditCompanyId(String(f.company));
    setEditMagap(f.magap || '');
    setEditHectares(hectaresText(f.hectares_in_production));
    setEditFumigated(hectaresText(f.fumigated_hectares));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditCompanyId('');
    setEditMagap('');
    setEditHectares('');
    setEditFumigated('');
  };

  const handleSaveEdit = async (id) => {
    if (!editName.trim() || !editCompanyId || !hectaresValid(editHectares) || !hectaresValid(editFumigated)) return;
    setSavingId(id);
    setStatus(null);
    try {
      const r = await api.patch(`farms/${id}/`, {
        name: editName.trim(),
        company: Number(editCompanyId),
        magap: editMagap.trim(),
        hectares_in_production: hectaresPayload(editHectares),
        fumigated_hectares: hectaresPayload(editFumigated),
      });
      setFarms((fs) => fs.map((f) => (f.id === id ? r.data : f)));
      cancelEdit();
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudieron guardar los cambios.' });
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta finca?')) return;
    setDeletingId(id);
    setStatus(null);
    try {
      await api.delete(`farms/${id}/`);
      setFarms((fs) => fs.filter((f) => f.id !== id));
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo eliminar la finca.' });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="companies-root">
      <h1 className="companies-title">🚜 Fincas</h1>

      <form className="companies-add-form companies-add-form-wide" onSubmit={handleCreate}>
        <input
          className="companies-add-input"
          placeholder="Nombre de la nueva finca"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <select
          className="companies-add-input"
          value={newCompanyId}
          onChange={(e) => setNewCompanyId(e.target.value)}
        >
          {companies.length === 0 && <option value="">Aún no hay empresas</option>}
          {companies.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <input
          className="companies-add-input"
          placeholder="MAGAP"
          value={newMagap}
          onChange={(e) => setNewMagap(e.target.value)}
        />
        <input
          className="companies-add-input"
          placeholder="Hectáreas en producción"
          inputMode="decimal"
          value={newHectares}
          onChange={(e) => setNewHectares(cleanHectares(e.target.value))}
          title={hectaresValid(newHectares) ? undefined : 'Hasta 8 enteros y 2 decimales'}
          style={hectaresValid(newHectares) ? undefined : { borderColor: '#b3261e' }}
        />
        <input
          className="companies-add-input"
          placeholder="Hectáreas fumigadas"
          inputMode="decimal"
          value={newFumigated}
          onChange={(e) => setNewFumigated(cleanHectares(e.target.value))}
          title={hectaresValid(newFumigated) ? undefined : 'Hasta 8 enteros y 2 decimales'}
          style={hectaresValid(newFumigated) ? undefined : { borderColor: '#b3261e' }}
        />
        <button
          className="companies-add-btn"
          type="submit"
          disabled={creating || !newName.trim() || !newCompanyId || !hectaresValid(newHectares) || !hectaresValid(newFumigated)}
        >
          {creating ? 'Agregando…' : '+ Agregar finca'}
        </button>
      </form>

      {status && <div className={`companies-status companies-status-${status.type}`}>{status.text}</div>}

      <div className="companies-card">
        {loading ? (
          <div className="companies-empty">Cargando…</div>
        ) : farms.length === 0 ? (
          <div className="companies-empty">Aún no hay fincas.</div>
        ) : (
          <table className="companies-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Empresa</th>
                <th>MAGAP</th>
                <th className="num">Hectáreas en producción</th>
                <th className="num">Hectáreas fumigadas</th>
                <th>Creado por</th>
                <th>Creado</th>
                <th>Editado por</th>
                <th>Modificado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {farms.map((f) => (
                <tr key={f.id}>
                  <td>
                    {editingId === f.id ? (
                      <input
                        className="companies-edit-input"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        autoFocus
                      />
                    ) : (
                      f.name
                    )}
                  </td>
                  <td>
                    {editingId === f.id ? (
                      <select
                        className="companies-edit-input"
                        value={editCompanyId}
                        onChange={(e) => setEditCompanyId(e.target.value)}
                      >
                        {companies.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    ) : (
                      f.company_name
                    )}
                  </td>
                  <td>
                    {editingId === f.id ? (
                      <input
                        className="companies-edit-input"
                        value={editMagap}
                        onChange={(e) => setEditMagap(e.target.value)}
                      />
                    ) : (
                      f.magap || '—'
                    )}
                  </td>
                  <td className="num">
                    {editingId === f.id ? (
                      <input
                        className="companies-edit-input"
                        inputMode="decimal"
                        value={editHectares}
                        onChange={(e) => setEditHectares(cleanHectares(e.target.value))}
                        title={hectaresValid(editHectares) ? undefined : 'Hasta 8 enteros y 2 decimales'}
                        style={hectaresValid(editHectares) ? undefined : { borderColor: '#b3261e' }}
                      />
                    ) : (
                      formatHectares(f.hectares_in_production)
                    )}
                  </td>
                  <td className="num">
                    {editingId === f.id ? (
                      <input
                        className="companies-edit-input"
                        inputMode="decimal"
                        value={editFumigated}
                        onChange={(e) => setEditFumigated(cleanHectares(e.target.value))}
                        title={hectaresValid(editFumigated) ? undefined : 'Hasta 8 enteros y 2 decimales'}
                        style={hectaresValid(editFumigated) ? undefined : { borderColor: '#b3261e' }}
                      />
                    ) : (
                      formatHectares(f.fumigated_hectares)
                    )}
                  </td>
                  <td>{f.created_by_username || '—'}</td>
                  <td>{new Date(f.created).toLocaleDateString()}</td>
                  <td>{f.last_edited_by_username || '—'}</td>
                  <td>{new Date(f.modified).toLocaleDateString()}</td>
                  <td>
                    <div className="row-actions">
                      {editingId === f.id ? (
                        <>
                          <button
                            className="row-icon-btn"
                            onClick={() => handleSaveEdit(f.id)}
                            disabled={savingId === f.id || !hectaresValid(editHectares) || !hectaresValid(editFumigated)}
                            title="Guardar"
                            aria-label="Guardar"
                          >
                            {savingId === f.id ? '…' : '💾'}
                          </button>
                          <button className="row-icon-btn" onClick={cancelEdit} title="Cancelar" aria-label="Cancelar">✖️</button>
                        </>
                      ) : (
                        <>
                          <button className="row-icon-btn" onClick={() => startEdit(f)} title="Editar" aria-label="Editar">✏️</button>
                          <button
                            className="row-icon-btn"
                            onClick={() => handleDelete(f.id)}
                            disabled={deletingId === f.id}
                            title="Eliminar"
                            aria-label="Eliminar"
                          >
                            {deletingId === f.id ? '…' : '🗑️'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
